import { Box, Button, Checkbox, Paper, Typography } from '@mui/material';
import { EntityType } from '@nohara-dxdev/mt-common';
import { createContext, type HTMLAttributes, useContext, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { SelectElement, AutocompleteElement } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';

import { useBuildings } from '@/features/buildings/api/get-buildings';
import { useItemSelectorItems } from '@/features/items/api/get-items';

export interface EntityFormData {
  entityType: EntityType;
  entityIds: string[];
}

interface ItemOptionsPaginationContextValue {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  loadMoreLabel: string;
}

const ItemOptionsPaginationContext = createContext<ItemOptionsPaginationContextValue | null>(null);

const ItemOptionsPaper = ({ children, ...props }: HTMLAttributes<HTMLElement>) => {
  const pagination = useContext(ItemOptionsPaginationContext);

  return (
    <Paper {...props}>
      {children}
      {pagination?.hasNextPage && (
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
          <Button
            size="small"
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void pagination.fetchNextPage();
            }}
            disabled={pagination.isFetchingNextPage}
          >
            {pagination.loadMoreLabel}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export const EntitySelector = () => {
  const { t } = useTranslation();
  const form = useFormContext<EntityFormData>();
  const selectedEntityType = form.watch('entityType');
  const { data: buildings, isLoading: isLoadingBuildings } = useBuildings();
  const itemQuery = useItemSelectorItems(selectedEntityType === 'item');
  const items = useMemo(
    () => itemQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [itemQuery.data],
  );

  // Flatten all entities for search
  const allEntities = useMemo(() => {
    const entities: {
      id: string;
      name: string;
      type: EntityType;
      path?: string;
    }[] = [];

    // Add buildings
    if (buildings) {
      buildings.forEach((building) => {
        entities.push({
          id: building.id,
          name: building.name,
          type: 'building',
          path: building.name,
        });

        // Add floors
        building.floors.forEach((floor) => {
          entities.push({
            id: floor.id,
            name: floor.name,
            type: 'floor',
            path: `${building.name} / ${floor.name}`,
          });

          // Add rooms
          floor.rooms.forEach((room) => {
            entities.push({
              id: room.id,
              name: room.name,
              type: 'room',
              path: `${building.name} / ${floor.name} / ${room.name}`,
            });
          });
        });
      });
    }

    // Add items
    items.forEach((item) => {
      entities.push({
        id: item.id,
        name: item.name,
        type: 'item',
        path: item.name,
      });
    });
    return entities;
  }, [buildings, items]);

  // Filter entities by selected type
  const filteredEntities = useMemo(() => {
    return allEntities.filter((entity) => entity.type === selectedEntityType);
  }, [allEntities, selectedEntityType]);

  const isLoading =
    isLoadingBuildings ||
    (selectedEntityType === 'item' && (itemQuery.isLoading || itemQuery.isFetchingNextPage));
  const isDisabled = isLoadingBuildings || (selectedEntityType === 'item' && itemQuery.isLoading);
  const itemOptionsPagination = useMemo(
    () => ({
      hasNextPage: selectedEntityType === 'item' && itemQuery.hasNextPage,
      isFetchingNextPage: itemQuery.isFetchingNextPage,
      fetchNextPage: itemQuery.fetchNextPage,
      loadMoreLabel: t('tasks.create.loadMoreItems'),
    }),
    [
      itemQuery.fetchNextPage,
      itemQuery.hasNextPage,
      itemQuery.isFetchingNextPage,
      selectedEntityType,
      t,
    ],
  );

  return (
    <ItemOptionsPaginationContext.Provider value={itemOptionsPagination}>
      {/* Entity Type Selection */}
      <SelectElement
        name="entityType"
        label={t('tasks.create.entityType')}
        options={[
          { id: 'item', label: t('conversations.entityType.item') },
          { id: 'building', label: t('conversations.entityType.building') },
          { id: 'floor', label: t('conversations.entityType.floor') },
          { id: 'room', label: t('conversations.entityType.room') },
        ]}
        onChange={(_value) => {
          form.setValue('entityIds', []);
        }}
      />

      {/* Entity Selection: multi-select for bulk task creation. matchId stores plain entityIds. */}
      <AutocompleteElement
        name="entityIds"
        options={filteredEntities}
        label={t('tasks.create.selectEntity')}
        loading={isLoading}
        multiple
        matchId
        textFieldProps={{
          helperText: t('tasks.create.selectEntityHelper'),
        }}
        autocompleteProps={{
          disabled: isDisabled,
          disableCloseOnSelect: true,
          getOptionLabel: (option) => option.path ?? option.name,
          getOptionKey: (option) => option.id,
          slots: { paper: ItemOptionsPaper },
          renderOption: (props, option, { selected }) => (
            <li {...props} key={option.id}>
              <Checkbox checked={selected} sx={{ mr: 1 }} />
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {option.name}
                </Typography>
                {option.path && option.path !== option.name && (
                  <Typography variant="caption" color="text.secondary">
                    {option.path}
                  </Typography>
                )}
              </Box>
            </li>
          ),
          filterOptions: (options, { inputValue }) => {
            return options.filter(
              (option) =>
                option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                option.path?.toLowerCase().includes(inputValue.toLowerCase()),
            );
          },
          isOptionEqualToValue: (option, value) => option.id === value.id,
        }}
      />
    </ItemOptionsPaginationContext.Provider>
  );
};
